import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./create-checkpoint.dialog.scoped.scss";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AppButtonComponent from "../../../../components/form/app-button";
import CloseIcon from "@material-ui/icons/Close";
import { showPopupToastMsg, showToastMsg } from "../../../../helpers";
import { services } from "../../../../services";
import AppSwitchComponent from "../../../../components/form/app-switch";
import AppRadioComponent from "../../../../components/form/app-radio";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import useSections from "../../hooks/useSections.hook";

const FIELD_LIMITS = {
  NAME: 80,
  DESCRIPTION: 1200,
} as const;

const PASS_SCORE_LIST = [
  {
    value: 60,
    label: "60%",
  },
  {
    value: 80,
    label: "80%",
  },
  {
    value: 90,
    label: "90%",
  },
];

interface CheckpointFormData {
  title: string;
  description: string;
  sectionId: number;
  status: string;
  sequential: string;
  visibility: string;
  passingScore: number;
}

interface FormErrors {
  title?: string;
  description?: string;
  [key: string]: string | undefined;
}

export interface CreateCheckpointDialogProps {
  show: boolean;
  onClose: (success?: boolean) => void;
  sectionId?: number;
  checkpointId?: number;
  checkpointData?: any;
  courseId: number;
}

export const CreateCheckpointDialog: React.FC<CreateCheckpointDialogProps> = ({
  show,
  onClose,
  sectionId,
  checkpointId,
  checkpointData,
  courseId,
}) => {
  const { t } = useTranslation();
  const { sections } = useSections(courseId);
  const isEditMode = useMemo(
    () => checkpointId != null && checkpointData != null,
    [checkpointId, checkpointData],
  );

  const [formData, setFormData] = useState<CheckpointFormData>({
    title: "",
    description: "",
    sectionId: sectionId ?? 0,
    status: "ACTIVE",
    sequential: "NON_SEQUENTIAL",
    visibility: "PUBLIC",
    passingScore: 0,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  const validateField = useCallback(
    (name: keyof CheckpointFormData, value: unknown): string => {
      switch (name) {
        case "title":
          if (!value || String(value).trim() === "") {
            return t("EDUCATION_PAGE.CHECKPOINT_NAME_REQUIRED");
          }
          if (String(value).length > FIELD_LIMITS.NAME) {
            return t("EDUCATION_PAGE.CHECKPOINT_NAME_TOO_LONG", {
              max: FIELD_LIMITS.NAME,
            });
          }
          return "";
        case "description":
          if (!value || String(value).trim() === "") {
            return t("EDUCATION_PAGE.CHECKPOINT_DESCRIPTION_REQUIRED");
          }
          if (String(value).length > FIELD_LIMITS.DESCRIPTION) {
            return t("EDUCATION_PAGE.CHECKPOINT_DESCRIPTION_TOO_LONG", {
              max: FIELD_LIMITS.DESCRIPTION,
            });
          }
          return "";
        default:
          return "";
      }
    },
    [t],
  );

  const resetForm = useCallback(() => {
    setFormData({
      title: "",
      description: "",
      sectionId: sectionId ?? 0,
      status: "ACTIVE",
      sequential: "NON_SEQUENTIAL",
      visibility: "PUBLIC",
      passingScore: 0,
    });
    setErrors({});
  }, [sectionId]);

  const handleClose = useCallback(
    (success: boolean = false) => {
      if (!saving) {
        resetForm();
        onClose(success);
      }
    },
    [saving, resetForm, onClose],
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | { name?: string; value: unknown }
      >,
    ) => {
      const { name, value } = e.target;
      if (!name) return;

      const fieldName = name as keyof CheckpointFormData;
      const processedValue = typeof value === "string" ? value : String(value);

      setFormData((prev: CheckpointFormData) => ({
        ...prev,
        [fieldName]: processedValue,
      }));

      const error = validateField(fieldName, processedValue);
      setErrors((prev: FormErrors) => ({
        ...prev,
        [fieldName]: error,
      }));
    },
    [validateField],
  );

  /**
   * Handle select dropdown changes
   * Select component has different onChange signature: (event, value)
   */
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const { name, value } = e.target;
      if (!name) return;

      const fieldName = name as keyof CheckpointFormData;
      const numValue = typeof value === "number" ? value : Number(value);

      setFormData((prev) => ({ ...prev, [fieldName]: numValue }));

      // Validate field
      const error = validateField(fieldName, numValue);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    },
    [validateField],
  );

  const handleSubmit = useCallback(async () => {
    const finalErrors: FormErrors = {};
    (["title", "description"] as const).forEach((key) => {
      const err = validateField(key, formData[key]);
      if (err) finalErrors[key] = err;
    });

    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      showToastMsg("error", t("EDUCATION_PAGE.PLEASE_FILL_ALL_FIELDS"));
      return;
    }

    const payload = {
      title: formData.title.trim(),
      description: formData.description,
      status: formData.status,
      sequential: formData.sequential,
      visibility: formData.visibility,
      sectionId: formData.sectionId
        ? String(formData.sectionId)
        : String(sectionId),
      passingScore: formData.passingScore,
    };

    setSaving(true);
    try {
      let response: any;
      if (isEditMode && checkpointId != null) {
        response = await services.courses.updateCheckpoint(
          String(checkpointId),
          payload,
        );
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.CHECKPOINT_UPDATED_SUCCESSFULLY"),
          );
          handleClose(true);
        } else {
          showPopupToastMsg(
            "error",
            response?.message || t("SOME_THING_WENT_WRONG"),
          );
        }
      } else {
        response = await services.courses.addCheckpoint(payload);
        if (response && response.message === "SUCCESS") {
          showToastMsg(
            "success",
            t("EDUCATION_PAGE.CHECKPOINT_CREATED_SUCCESSFULLY"),
          );
          handleClose(true);
        } else {
          showPopupToastMsg(
            "error",
            response?.message || t("SOME_THING_WENT_WRONG"),
          );
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} checkpoint:`,
        error,
      );
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSaving(false);
    }
  }, [
    sectionId,
    formData,
    handleClose,
    isEditMode,
    checkpointId,
    validateField,
    t,
  ]);

  const isFormValid = useMemo(() => {
    const titleOk =
      formData.title.trim() !== "" &&
      formData.title.length <= FIELD_LIMITS.NAME;
    const descriptionOk =
      formData.description.trim() !== "" &&
      formData.description.length <= FIELD_LIMITS.DESCRIPTION;
    const noFieldErrors = !errors.title && !errors.description;
    return titleOk && descriptionOk && noFieldErrors;
  }, [formData.title, formData.description, errors.title, errors.description]);

  useEffect(() => {
    if (!show) return;
    if (isEditMode && checkpointData) {
      setFormData({
        title: checkpointData.title ?? "",
        description: checkpointData.description ?? "",
        status: checkpointData.status ?? "INACTIVE",
        sequential: checkpointData.sequential ?? "NON_SEQUENTIAL",
        visibility: checkpointData.visibility ?? "PUBLIC",
        sectionId: checkpointData.sectionId ?? 0,
        passingScore: checkpointData.passingScore ?? 0,
      } as CheckpointFormData);
      setErrors({});
    } else if (!isEditMode) {
      resetForm();
    }
  }, [show, isEditMode, checkpointData, resetForm]);

  return (
    <Dialog
      open={show}
      onClose={() => !saving && handleClose(false)}
      maxWidth="md"
      fullWidth
      disableBackdropClick={saving}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
    >
      <DialogTitle
        style={{
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        disableTypography
      >
        <Typography variant="h2" className="m-0">
          {isEditMode
            ? t("EDUCATION_PAGE.UPDATE_CHECKPOINT")
            : t("EDUCATION_PAGE.ADD_CHECKPOINT")}
        </Typography>

        <IconButton
          aria-label={t("CLOSE")}
          edge="end"
          onClick={() => handleClose(false)}
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Checkpoint Details */}
        <Box className="title">
          <Typography
            variant="h3"
            className="font-weight-bold"
            color="textPrimary"
          >
            {t("EDUCATION_PAGE.CHECKPOINT_DETAILS")}
          </Typography>
        </Box>

        {/* Name */}
        <Box className="grid-2 mb-3">
          <Typography variant="body1">
            {t("EDUCATION_PAGE.CHECKPOINT_NAME")}*
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              variant="standard"
              placeholder={t("EDUCATION_PAGE.CHECKPOINT_NAME")}
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={saving}
              inputProps={{
                maxLength: FIELD_LIMITS.NAME,
              }}
            />

            <Box className="hint d-flex align-items-center justify-content-between">
              <Typography variant="caption" color="error">
                {errors.title ?? ""}
              </Typography>
              <Typography
                variant="body2"
                className="px-2 text-right"
                style={{ flex: "auto" }}
              >
                {formData.title.length}/{FIELD_LIMITS.NAME}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Description */}
        <Box className="grid-2 mb-3">
          <Typography variant="body1">
            {t("EDUCATION_PAGE.CHECKPOINT_DESCRIPTION")}*
          </Typography>
          <Box className="standard_text_input">
            <TextField
              className="w-100"
              variant="standard"
              placeholder={t("EDUCATION_PAGE.CHECKPOINT_DESCRIPTION")}
              multiline
              name="description"
              rows={12}
              value={formData.description}
              onChange={handleInputChange}
              inputProps={{
                maxLength: FIELD_LIMITS.DESCRIPTION,
              }}
            />

            <Box className="hint d-flex align-items-center justify-content-between">
              {Boolean(errors.description) && (
                <Typography variant="caption" color="error">
                  {errors.description}
                </Typography>
              )}
              <Typography
                variant="body2"
                className="px-2 text-right"
                style={{ flex: "auto" }}
              >
                {formData.description.length}/{FIELD_LIMITS.DESCRIPTION}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="grid-2 mb-3">
          <Typography variant="body1">{t("EDUCATION_PAGE.SECTION")}</Typography>
          <Box className="standard_text_input dropdown">
            <FormControl className="select-input select-grey w-100">
              <Select
                name="sectionId"
                onChange={handleSelectChange}
                IconComponent={ExpandMoreIcon}
                value={formData.sectionId}
                error={Boolean(errors.sectionId)}
              >
                <MenuItem value={0} disabled>
                  {t("EDUCATION_PAGE.SELECT_SECTION")}
                </MenuItem>
                {sections.map((value: any, index: number) => (
                  <MenuItem
                    key={index}
                    value={value.id}
                    disabled={value.checkpoints.length > 0}
                  >
                    {t(value.title)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Checkpoint Settings */}
        <Box className="title">
          <Typography
            variant="h3"
            className="font-weight-bold"
            color="textPrimary"
          >
            {t("EDUCATION_PAGE.CHECKPOINT_SETTINGS")}
          </Typography>
        </Box>
        {/* Status */}
        <Box className="grid-2 mb-3">
          <Typography variant="body1">
            {t("EDUCATION_PAGE.VISIBILITY")}
          </Typography>
          <Box className="standard_text_input">
            <AppSwitchComponent
              label={t("EDUCATION_PAGE.VISIBLE")}
              onChange={(e: any) => {
                const { checked } = e.target;
                setFormData((prev: CheckpointFormData) => ({
                  ...prev,
                  status: checked ? "ACTIVE" : "INACTIVE",
                }));
              }}
              checked={formData.status === "ACTIVE"}
              name="status"
              size="small"
              color="primary"
            />
            <Typography variant="body2" className="mt-3" color="textPrimary">
              {t("EDUCATION_PAGE.TOGGLES_THE_DISPLAY_OF_THE_CHECKPOINT")}
            </Typography>
          </Box>
        </Box>

        {/* Sequential Options */}
        <Box className="grid-2 mb-3">
          <Typography variant="body1">
            {t("EDUCATION_PAGE.GRADUATION_OPTIONS")}
          </Typography>
          <Box>
            <AppRadioComponent
              style={{
                width: "fit-content",
              }}
              name="graduationOptions"
              value={formData.sequential}
              onChange={(value: string) => {
                if (value === "SCORE_BASED") {
                  setFormData((prev: CheckpointFormData) => ({
                    ...prev,
                    passingScore: PASS_SCORE_LIST[0].value,
                  }));
                }
                setFormData((prev: CheckpointFormData) => ({
                  ...prev,
                  sequential: value,
                }));
              }}
              options={[
                {
                  value: "NON_SEQUENTIAL",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>{t("EDUCATION_PAGE.NONE")}</strong>
                      <br />
                      {t(
                        "EDUCATION_PAGE.COMPLETION_OF_THIS_CHECKPOINT_IS_NOT_TRACKED",
                      )}
                    </Typography>
                  ),
                },
                {
                  value: "SEQUENTIAL",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>{t("EDUCATION_PAGE.SEQUENTIAL")}</strong>
                      <br />
                      {t(
                        "EDUCATION_PAGE.MEMBERS_MUST_COMPLETE_THE_CHECKPOINT_TO_CONTINUE",
                      )}
                    </Typography>
                  ),
                },
                {
                  value: "SCORE_BASED",
                  label: (
                    <Typography variant="body1" color="textPrimary">
                      <strong>
                        {t("EDUCATION_PAGE.CONTINUE_ONLY_AFTER_PASSING")}
                      </strong>
                      <br />
                      {t(
                        "EDUCATION_PAGE.MEMBERS_MUST_COMPLETE_AND_PASS_THE_CHECKPOINT_TO_CONTINUE",
                      )}
                    </Typography>
                  ),
                },
              ]}
            />
            <Box className="standard_text_input dropdown mt-4">
              <FormControl className="select-input select-grey w-50">
                <Select
                  name="passingScore"
                  onChange={handleSelectChange}
                  IconComponent={ExpandMoreIcon}
                  value={formData.passingScore}
                  error={Boolean(errors.passingScore)}
                >
                  <MenuItem value={0} disabled>
                    {t("EDUCATION_PAGE.SET_THE_PASSING_SCORE")}
                  </MenuItem>
                  {PASS_SCORE_LIST.map((value: any, index: number) => (
                    <MenuItem key={index} value={value.value}>
                      {value.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* Access and Visibility */}
        <Box className="title">
          <Typography
            variant="h3"
            color="textPrimary"
            className="font-weight-bold"
          >
            {t("EDUCATION_PAGE.ACCESS_AND_VISIBILITY")}
          </Typography>
        </Box>

        {/* Public */}
        <Box className="grid-2 mb-3">
          <Typography variant="body1" color="textPrimary">
            {t("EDUCATION_PAGE.CHECKPOINT_VISIBILITY")}
          </Typography>
          <AppRadioComponent
            name="checkpointVisibility"
            value={formData.visibility}
            onChange={(value: string) =>
              setFormData((prev: CheckpointFormData) => ({
                ...prev,
                visibility: value,
              }))
            }
            options={[
              {
                value: "PUBLIC",
                label: t("EDUCATION_PAGE.ALL_COURSE_MEMBERS"),
              },
              {
                value: "ADMIN_ONLY",
                label: t("EDUCATION_PAGE.ONLY_ADMINS"),
              },
            ]}
          />
        </Box>
      </DialogContent>
      <DialogActions
        style={{
          borderTop: "1px solid var(--border)",
          padding: "16px 24px",
          gap: "1rem",
        }}
      >
        <AppButtonComponent
          variant="text"
          color="secondary"
          onClick={() => handleClose(false)}
          disabled={saving}
        >
          {t("CANCEL")}
        </AppButtonComponent>
        <AppButtonComponent
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving || !isFormValid}
        >
          {isEditMode ? t("SAVE") : t("CREATE")}
        </AppButtonComponent>
      </DialogActions>
    </Dialog>
  );
};
